import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash2, Search, Plus, Archive, ArchiveRestore, AlertTriangle, ImageOff } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  adminListProducts,
  adminUploadImage,
  adminListCategories,
  adminListTypes,
  adminDeleteProduct,
  adminDuplicateProduct,
  adminBulkProducts,
} from "../../services/api";
import { getProductImage } from "../../data/images";
import { formatINR } from "../../data/content";

const PAGE_SIZE = 24;

const stockState = (p) => {
  if (p.stock == null) return "unset";
  if (p.stock <= 0) return "out";
  if (p.stock <= (p.low_stock_alert || 5)) return "low";
  return "ok";
};

export default function AdminProducts() {
  const { user, ready } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, all_total: 0, needs_review: 0 });
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(searchParams.get("category") || "all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [stock, setStock] = useState("all");
  const [photos, setPhotos] = useState("all");
  const [quickBusy, setQuickBusy] = useState("");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  useEffect(() => { const t = setTimeout(() => setQ(qDraft.trim()), 350); return () => clearTimeout(t); }, [qDraft]);
  useEffect(() => { setPage(1); }, [q, cat, type, status, stock, photos, sort]);

  const load = () => {
    setLoading(true);
    adminListProducts({ q, category: cat, type, status, stock, photos, sort, page, limit: PAGE_SIZE })
      .then(setData)
      .catch(() => toast.error("Could not load bracelets."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!ready || user?.role !== "admin") return;
    Promise.all([adminListCategories(), adminListTypes()]).then(([c, t]) => { setCategories(c); setTypes(t); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  useEffect(() => {
    if (!ready || user?.role !== "admin") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user, q, cat, type, status, stock, photos, sort, page]);

  const catLabel = useMemo(() => Object.fromEntries(categories.map((c) => [c.slug, c.name])), [categories]);
  const typeLabel = useMemo(() => Object.fromEntries(types.map((t) => [t.slug, t.name])), [types]);
  const rows = data.items;

  const toggle = (slug) => setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  const allOnPageSelected = rows.length > 0 && rows.every((p) => selected.includes(p.slug));
  const toggleAll = () =>
    setSelected((s) => (allOnPageSelected ? s.filter((x) => !rows.some((p) => p.slug === x)) : [...new Set([...s, ...rows.map((p) => p.slug)])]));

  const runBulk = async (action) => {
    if (!selected.length) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${selected.length} bracelet(s)? Prefer Archive to keep records.`)) return;
    const body = { slugs: selected, action };
    if (action === "set_stock") {
      const v = window.prompt("Set stock quantity for selected bracelets:");
      if (v === null) return;
      body.value = Number(v) || 0;
    }
    try {
      const res = await adminBulkProducts(body);
      toast.success(`${action.replace("_", " ")}: ${res.affected} updated${res.skipped ? `, ${res.skipped} skipped` : ""}`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk action failed.");
    }
  };

  const duplicate = async (slug) => {
    try { await adminDuplicateProduct(slug); toast.success("Bracelet duplicated as draft."); load(); }
    catch { toast.error("Duplicate failed."); }
  };
  const archiveOne = async (slug, restore = false) => {
    try { await adminBulkProducts({ slugs: [slug], action: restore ? "unarchive" : "archive" }); toast.success(restore ? "Restored." : "Archived."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed."); }
  };
  const remove = async (slug, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    try { await adminDeleteProduct(slug); toast.success("Bracelet deleted."); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed."); }
  };

  const quickPhoto = async (slug, file) => {
    if (!file) return;
    setQuickBusy(slug);
    try {
      await adminUploadImage(slug, file);
      toast.success("Photo attached.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setQuickBusy("");
    }
  };

  const selectClass = "border border-brand-sand bg-brand-surface px-3 py-2 text-sm text-brand-charcoal outline-none focus:border-brand-orange";
  const badge = (p) => ({ ok: "bg-brand-orange/10 text-brand-orange", low: "bg-amber-100 text-amber-700", out: "bg-red-100 text-red-700", unset: "bg-brand-sand/50 text-brand-stone" }[stockState(p)]);

  return (
    <AdminLayout
      title="Bracelets"
      subtitle={`${data.total} of ${data.all_total} shown${data.needs_review ? ` · ${data.needs_review} need type review` : ""}`}
      actions={
        <div className="flex items-center gap-2">
          <Link to="/admin/bulk-images" data-testid="products-bulk-photos-btn" className="hidden items-center gap-2 border border-brand-charcoal/30 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal transition-colors hover:border-brand-orange hover:text-brand-orange sm:flex">
            Bulk photos
          </Link>
          <Link to="/admin/products/new" data-testid="products-add-btn" className="flex items-center gap-2 bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream transition-colors hover:bg-brand-orange">
            <Plus className="h-4 w-4" /> Add bracelet
          </Link>
        </div>
      }
    >
      <div data-testid="admin-products-page">
        <div className="flex flex-col gap-3 border border-brand-sand bg-brand-surface p-4 md:flex-row md:flex-wrap md:items-center">
          <div className="relative flex-1 md:min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-stone" />
            <input data-testid="products-search" value={qDraft} onChange={(e) => setQDraft(e.target.value)} placeholder="Search name or SKU" className="w-full border border-brand-sand bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-orange" />
          </div>
          <select data-testid="filter-category" className={selectClass} value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select data-testid="filter-type" className={selectClass} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="all">All types</option>
            <option value="missing">Needs review (no type)</option>
            {types.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
          <select data-testid="filter-status" className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">Active (all)</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select data-testid="filter-stock" className={selectClass} value={stock} onChange={(e) => setStock(e.target.value)}>
            <option value="all">All stock</option>
            <option value="ok">In stock</option>
            <option value="low">Low</option>
            <option value="out">Out</option>
            <option value="unset">Unset</option>
          </select>
          <select data-testid="filter-photos" className={selectClass} value={photos} onChange={(e) => setPhotos(e.target.value)}>
            <option value="all">All photos</option>
            <option value="without">No photos</option>
            <option value="with">Has photos</option>
          </select>
          <select data-testid="sort-by" className={selectClass} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="latest">Latest</option>
            <option value="price">Price</option>
            <option value="name">Name</option>
          </select>
        </div>

        {selected.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border border-brand-orange/40 bg-brand-orange/5 px-4 py-3" data-testid="bulk-bar">
            <span className="text-sm font-medium text-brand-charcoal">{selected.length} selected</span>
            <button onClick={() => runBulk("publish")} data-testid="bulk-publish" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-orange hover:underline">Publish</button>
            <button onClick={() => runBulk("unpublish")} data-testid="bulk-unpublish" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal hover:underline">Unpublish</button>
            <button onClick={() => runBulk("set_stock")} data-testid="bulk-set-stock" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal hover:underline">Set stock</button>
            {status === "archived"
              ? <button onClick={() => runBulk("unarchive")} data-testid="bulk-unarchive" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal hover:underline">Restore</button>
              : <button onClick={() => runBulk("archive")} data-testid="bulk-archive" className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal hover:underline">Archive</button>}
            <button onClick={() => runBulk("delete")} data-testid="bulk-delete" className="text-xs font-semibold uppercase tracking-[0.12em] text-red-600 hover:underline">Delete</button>
          </div>
        )}

        <div className="mt-4 overflow-x-auto border border-brand-sand bg-brand-surface" data-testid="products-table">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-brand-sand text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-stone">
                <th className="p-3"><input type="checkbox" data-testid="select-all" checked={allOnPageSelected} onChange={toggleAll} className="h-4 w-4 accent-brand-orange" /></th>
                <th className="p-3">Bracelet</th>
                <th className="p-3">Category</th>
                <th className="p-3">Type</th>
                <th className="p-3">Photos</th>
                <th className="p-3">Price</th>
                <th className="p-3">Stock</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-8 text-center text-brand-stone" data-testid="products-loading">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-brand-stone" data-testid="products-empty">No bracelets match your filters.</td></tr>
              ) : rows.map((p) => (
                <tr key={p.slug} className="border-b border-brand-sand/50 hover:bg-brand-cream/40" data-testid={`product-row-${p.slug}`}>
                  <td className="p-3"><input type="checkbox" data-testid={`select-${p.slug}`} checked={selected.includes(p.slug)} onChange={() => toggle(p.slug)} className="h-4 w-4 accent-brand-orange" /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 shrink-0 overflow-hidden bg-white">
                        {p.image ? <img src={getProductImage(p.image, p.name).src} alt={p.name} className="h-full w-full object-contain p-1" />
                          : <div className="flex h-full items-center justify-center font-serif text-brand-orange/50">H</div>}
                      </div>
                      <div>
                        <Link to={`/admin/products/${p.slug}`} className="font-medium text-brand-charcoal hover:text-brand-orange">{p.name}</Link>
                        <p className="text-[11px] text-brand-stone">{p.sku || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-brand-stone">{catLabel[p.category] || p.category}</td>
                  <td className="p-3">
                    {p.type ? <span className="text-brand-stone">{typeLabel[p.type] || p.type}</span>
                      : <span className="inline-flex items-center gap-1 bg-amber-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700" data-testid={`needs-review-${p.slug}`}><AlertTriangle className="h-3 w-3" /> Needs review</span>}
                  </td>
                  <td className="p-3">
                    {(p.images || []).filter(Boolean).length === 0 ? (
                      <label data-testid={`needs-photos-${p.slug}`} title="Click to attach a photo right now" className="inline-flex cursor-pointer items-center gap-1 bg-amber-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700 transition-colors hover:bg-amber-200">
                        <ImageOff className="h-3 w-3" /> {quickBusy === p.slug ? "Uploading…" : "Needs photos"}
                        <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid={`quick-photo-input-${p.slug}`} onChange={(e) => { quickPhoto(p.slug, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                    ) : (
                      <span className="text-brand-stone">{(p.images || []).filter(Boolean).length}/4</span>
                    )}
                  </td>
                  <td className="p-3 text-brand-charcoal">{p.price != null ? formatINR(p.price) : "—"}</td>
                  <td className="p-3"><span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${badge(p)}`}>{p.stock != null ? p.stock : "—"}</span></td>
                  <td className="p-3">
                    <span className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${p.archived ? "bg-brand-sand text-brand-stone" : p.published === false ? "bg-brand-gold/25 text-brand-charcoal" : "bg-brand-orange/10 text-brand-orange"}`} data-testid={`status-${p.slug}`}>
                      {p.archived ? "Archived" : p.published === false ? "Draft" : "Published"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-3">
                      <Link to={`/admin/products/${p.slug}`} data-testid={`edit-${p.slug}`} className="text-xs font-semibold uppercase tracking-[0.1em] text-brand-orange hover:underline">Edit</Link>
                      <button onClick={() => duplicate(p.slug)} data-testid={`duplicate-${p.slug}`} title="Duplicate" className="text-brand-stone hover:text-brand-orange"><Copy className="h-4 w-4" /></button>
                      {p.archived
                        ? <button onClick={() => archiveOne(p.slug, true)} data-testid={`unarchive-${p.slug}`} title="Restore" className="text-brand-stone hover:text-brand-orange"><ArchiveRestore className="h-4 w-4" /></button>
                        : <button onClick={() => archiveOne(p.slug)} data-testid={`archive-${p.slug}`} title="Archive" className="text-brand-stone hover:text-brand-orange"><Archive className="h-4 w-4" /></button>}
                      <button onClick={() => remove(p.slug, p.name)} data-testid={`delete-${p.slug}`} title="Delete permanently" className="text-brand-stone hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs text-brand-stone" data-testid="pagination-info">Page {data.page} of {data.pages} · {PAGE_SIZE} per page</p>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev" className="border border-brand-sand px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal transition-colors hover:border-brand-orange disabled:opacity-40">Prev</button>
            <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next" className="border border-brand-sand px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-brand-charcoal transition-colors hover:border-brand-orange disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
